import React from "react";
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';

import { GetApi } from "../../../api/api";
import {
  Project,
  FeatureCatalog,
  StreamVersions
} from "../../../api/types";
import { Feedback } from "../../../components/Validator";
import AppContext from "../../../components/AppContext";
import withModal, { WithModalProps, InjectedProps } from "../../../components/HOC/WithModal";
import StepForm, { Step } from '../../../components/StepForm';
import Pending from '../../../components/Pending';

import FeatureCatalogValidator from './FeatureCatalogValidator';
import { isVersionLowerOrEqual } from "../../../util/featureCatalogUtils";


const nameId = 'nameInputFeatureCatalog';
const descriptionId = 'descriptionInputFeatureCatalog';
const streamVersionId = 'streamVersionFeatureCatalog';
const baseFcId = 'baseSelectFeatureCatalog';

interface InnerEditProps extends EditProps {
  featureCatalog: Project
  baseFeatureCatalogs: Array<{ name: string, id: string, streamVersion: string }>
}

interface InnerEditState {
  featureCatalog: Project
  baseFeatureCatalogs: Array<{ name: string, id: string, streamVersion: string }>
  valid: Feedback
  disabled: boolean
}

class InnerEdit extends React.Component<InnerEditProps, InnerEditState> {
  public static contextType = AppContext;

  constructor(props: InnerEditProps) {
    super(props);
    this.state = {
      featureCatalog: props.featureCatalog,
      baseFeatureCatalogs: props.baseFeatureCatalogs,
      valid: props.valid,
      disabled: props.disabled
    }
  }

  public render() {
    return (
      <StepForm>
        <Step name="Basic information">{this.renderBasicInfo()}</Step>
        <Step name="Base catalog assignment">{this.renderBaseFcAssign()}</Step>
      </StepForm>
    );
  }

  private renderBasicInfo = () => {
    const disabledName = this.state.disabled || (!this.state.valid.valid && this.state.valid.id !== nameId)
    const disabledDescription = this.state.disabled || (!this.state.valid.valid && this.state.valid.id !== descriptionId)
    const disabledStreamVersion = this.state.disabled || !this.state.valid.valid
    const baseFcStreamVersion = this.state.featureCatalog.baseFeatureCatalogs[0].streamVersion || StreamVersions.SV_4110

    const inValidName = this.state.valid.id === nameId;
    const inValidDescription = this.state.valid.id === descriptionId;
    
    return (
      <Form>
        <Form.Group as={Row} controlId={nameId}>
          <Form.Label column={true} md='3'>
            Name{' '}<span className="text-danger">*</span>
          </Form.Label>
          <Col md='9'>
            <Form.Control
              type='text'
              value={this.state.featureCatalog.name}
              onChange={this.setName}
              onBlur={this.updateFeatureCatalogCustomization}
              disabled={disabledName}
              isInvalid={inValidName}
            />
            <Form.Control.Feedback type='invalid'>
              {this.state.valid.message}
            </Form.Control.Feedback>
            <Form.Text>
              Name of the feature catalog.
            </Form.Text>
          </Col>
        </Form.Group>
        
        <Form.Group as={Row} controlId={descriptionId}>
          <Form.Label column={true} md='3'>Description</Form.Label>
          <Col md='9'>
            <Form.Control
              type='text'
              value={this.state.featureCatalog.description}
              onChange={this.setDescription}
              onBlur={this.updateFeatureCatalogCustomization}
              disabled={disabledDescription}
              isInvalid={inValidDescription}
            />
            <Form.Control.Feedback type='invalid'>
              {this.state.valid.message}
            </Form.Control.Feedback>
            <Form.Text>
              An optional description for the feature catalog.
            </Form.Text>
          </Col>
        </Form.Group>

        <Form.Group as={Row} controlId={streamVersionId}>
          <Form.Label column={true} md='3'>
            Compatibility
          </Form.Label>
          <Col md='9'>
            <Form.Control
              as='select'
              value={this.state.featureCatalog.streamVersion}
              onChange={this.setStreamVersion}
              disabled={disabledStreamVersion}
            >
              <option value={StreamVersions.SV_4110} disabled={!isVersionLowerOrEqual(baseFcStreamVersion, StreamVersions.SV_4110)}>All Quadri versions</option>
              <option value={StreamVersions.SV_4211} disabled={!isVersionLowerOrEqual(baseFcStreamVersion, StreamVersions.SV_4211)}>Quadri 2021 and newer (supports measure number)</option>
            </Form.Control>
            <Form.Control.Feedback type='invalid'>
              {this.state.valid.message}
            </Form.Control.Feedback>
            <Form.Text>
              The use of e.g. measure number requires a new feature catalog format that is not compatible with Quadri
              versions less than 2021.
            </Form.Text>
          </Col>
        </Form.Group>
      </Form>
    )
  }

  private renderBaseFcAssign = () => {
    const disabledBaseFc = this.state.disabled || !this.state.valid.valid
    const streamVersion = this.state.featureCatalog.streamVersion || StreamVersions.SV_4110;
    
    return (
      <Form>
        <Form.Group as={Row} controlId={baseFcId}>
          <Form.Label column={true} md='3'>
            Base catalog{' '}<span className="text-danger">*</span>
          </Form.Label>
          <Col md='9'>
            <Form.Control
              as='select'
              // className='custom-select'
              value={this.state.featureCatalog.baseFeatureCatalogs[0].id}
              onChange={this.setBaseFeatureCatalog}
              disabled={disabledBaseFc}
            >
              <option value='' disabled={true}>-- please select --</option>
              {this.state.baseFeatureCatalogs?.filter(fc => isVersionLowerOrEqual(fc.streamVersion, streamVersion)).map(i => (
                <option key={i.id} value={i.id}>{i.name}</option>
              ))}
            </Form.Control>
            <Form.Text>
              The base feature catalog to extend upon. Only newer versions can be selected.
            </Form.Text>
          </Col>
        </Form.Group>
      </Form>
    )
  }

  private updateFeatureCatalogCustomization = () => {
    if (this.state.valid.valid) {
      this.setDisabled(true);
      GetApi()
      .then(api => api.updateFeatureCatalogCustomization(
        this.props.fccId,
        this.state.featureCatalog,
      ))
      .catch(error => this.context.useToasts.addToast(error, 'danger'))
      .finally(() => this.setDisabled(false))
    }
  }

  private setName = ( e: any ) => {
    e.persist();
    const isValid = FeatureCatalogValidator.getFieldValidator('name')(nameId, e.target.value)
    this.setState(prevState => ({
      featureCatalog: {
        ...prevState.featureCatalog,
        name: e.target.value
      },
      valid: isValid
    }), () => this.props.setValid(isValid))
  }

  private setDescription = ( e: any ) => {
    e.persist();
    const isValid = FeatureCatalogValidator.getFieldValidator('description')(descriptionId, e.target.value)
    this.setState(prevState => ({
      featureCatalog: {
        ...prevState.featureCatalog,
        description: e.target.value
      },
      valid: isValid
    }), () => this.props.setValid(isValid))
  }

  private setStreamVersion = (e: any) => {
    e.persist();
    this.setState(prevState => ({
      featureCatalog: {
        ...prevState.featureCatalog,
        streamVersion: e.target.value
      }
    }),
    () => this.updateFeatureCatalogCustomization());
  }

  private setBaseFeatureCatalog = (e: any) => {
    e.persist();
    const bfc = this.state.baseFeatureCatalogs.find(fc => fc.id === e.target.value); 
    this.setState(prevState => ({
      featureCatalog: {
        ...prevState.featureCatalog,
        baseFeatureCatalogs: [ { id: e.target.value, streamVersion: bfc?.streamVersion } ]
      }
    }), () => this.updateFeatureCatalogCustomization());
  }

  private setDisabled = ( value: boolean ) => {
    this.props.setDisabled(value);
    this.setState({
      disabled: value
    });
  }
}

interface EditProps extends InjectedProps {
  fccId: string
}

const Edit = ( props: EditProps ) => {
  const getData = async (): Promise<InnerEditProps> => {
    const api = await GetApi();
    const featureCatalog = await api.getFeatureCatalogCustomization(props.fccId);
    let baseFeatureCatalogs: FeatureCatalog[];
    if (featureCatalog.version && featureCatalog.version > 1) {
      const base = featureCatalog.baseFeatureCatalogs[0]
      baseFeatureCatalogs = await api.getFeatureCatalogs(base.standard, base.version);
    } else {
      baseFeatureCatalogs = await api.getFeatureCatalogs(undefined, undefined, StreamVersions.SV_4211);
    }
    return {
      ...props,
      featureCatalog: {
        ...featureCatalog,
        description: featureCatalog.description || ''
      },
      baseFeatureCatalogs: baseFeatureCatalogs
        .sort((a, b) => (new Date(a.date) < new Date(b.date) ? 1 : -1))
        .map(fc => ({
          name: `${fc.name} ${fc.version} - ${fc.date}`,
          id: fc.id,
          streamVersion: fc.streamVersion || StreamVersions.SV_4211
        }))
    }
  }

  const renderSuccess = ( innerProps: InnerEditProps ) => <InnerEdit {...innerProps} />;

  return (
    <Pending
      getData={getData}
      renderSuccess={renderSuccess}
    />
  );
}

export interface EditModalProps extends WithModalProps {
  fccId: string
}

const EditModal = withModal(Edit);

export default EditModal;